import { useMemo, type FC } from 'react';
import { Controller, useFormContext } from 'react-hook-form';

import { useTranslation } from '@mint-lib/i18n';
import { TextArea } from '@mint-lib/tw-components/patterns';

import useUserListData from '../../../api/queries/useUserListData.js';
import { NewRequestFormValues } from '../../../types/newRequestForm.js';
import type { OptionItem } from '../../../types/shared.js';
import MultiSelectWithSearch from '../../MultiSelectWithSearch.jsx';
import RecipientsCommentsTabError from './RecipientsCommentsTabError.jsx';
import RecipientsCommentsTabSkeleton from './RecipientsCommentsTabSkeleton.jsx';

type Props = {
  isVisible: boolean;
};

const RecipientsCommentsTab: FC<Props> = ({ isVisible }) => {
  const { t } = useTranslation('@mint-lib/approval-flow-circuit_v2');
  const { control } = useFormContext<NewRequestFormValues>();

  const {
    data: userListData,
    isPending: isUserListPending,
    isError: isUserListError,
  } = useUserListData({
    enabled: isVisible,
  });

  const emailOptionList = useMemo<OptionItem[]>(() => {
    if (!userListData?.length) {
      return [];
    }

    return userListData.reduce<OptionItem[]>((acc, { isActive, email }) => {
      if (!isActive) {
        return acc;
      }

      acc.push({
        label: email,
        value: email,
      });

      return acc;
    }, []);
  }, [userListData]);

  if (isUserListPending) {
    return <RecipientsCommentsTabSkeleton />;
  }

  if (isUserListError) {
    return <RecipientsCommentsTabError />;
  }

  return (
    <div>
      <Controller
        control={control}
        name="carbonCopyEmails"
        render={({ field }) => {
          return (
            <MultiSelectWithSearch
              id="send-new-request-cc-select"
              label={t('Cc')}
              labelTooltip={t(
                'Set Cc that will also receive updates for this approval',
              )}
              a11yLabel="Select CC emails"
              value={field.value}
              onChange={field.onChange}
              options={emailOptionList}
            />
          );
        }}
      />
      <div className="mt-4">
        <Controller
          control={control}
          name="comments"
          render={({ field }) => {
            return (
              <TextArea
                data-dd-action-name="Comments input field"
                label={t('Comments')}
                className="min-h-36"
                value={field.value}
                onChange={(e) => field.onChange(e.target.value)}
              />
            );
          }}
        />
      </div>
    </div>
  );
};

export default RecipientsCommentsTab;
